import React, { useEffect, useState } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useParams } from 'react-router';

import { RootState } from '../../../app/rootReducer';
import { Decision, DecisionType } from '../../../common/constants/decision';
import { ApproveDrawer } from '../drawer/approve/ApproveDrawer';
import { DeclineDrawer } from '../drawer/decline/DeclineDrawer';
import { IneligibleDrawer } from '../drawer/ineligible/IneligibleDrawer';
import { PostponeDrawer } from '../drawer/postpone/PostponeDrawer';
import { WaiveDrawer } from '../drawer/waive/WaiveDrawer';
import { IDecision } from '../interfaces/IDecision';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

interface EditDecisionLeadingToCLRManagerProps {
  decisionToEdit: IDecision | null;
  onClose: () => void;
}

export const EditDecisionLeadingToCLRManager = ({
  decisionToEdit,
  onClose,
}: EditDecisionLeadingToCLRManagerProps) => {
  const [decision, setDecision] = useState(null as Decision | null);
  const [updateReason, setUpdateReason] = useState(null as string | null);

  const { id } = useParams<{ id: string }>();
  const applicationId = id || '';

  const allDecisions = useSelector(state => state.decision.decisions);
  const engineMaxCoverage = allDecisions?.find(
    d => !d.ignored && d.decisionType === DecisionType.ENGINE,
  )?.coverage;

  const isOpen = decisionToEdit !== null;

  // Set the decision type when decisionToEdit changes
  useEffect(() => {
    if (decisionToEdit) {
      setDecision(decisionToEdit.decision);
    } else {
      setDecision(null);
      setUpdateReason(null);
    }
  }, [decisionToEdit]);

  const handleClose = () => {
    setDecision(null);
    setUpdateReason(null);
    onClose();
  };

  if (!decisionToEdit) {
    return null;
  }

  return (
    <div style={{ position: 'relative', zIndex: 1050 }}>
      {decision === Decision.APPROVED && (
        <ApproveDrawer
          isOpen={isOpen}
          setIsOpen={handleClose}
          applicationId={applicationId}
          hasFinalDecision={false}
          hasPIADecision={true}
          editedDecision={decisionToEdit}
          setDecision={setDecision}
          updateReason={updateReason}
          setUpdateReason={setUpdateReason}
          engineMaxCoverage={engineMaxCoverage}
          isRUW={false}
          isEditLeadingToCLR={true}
        />
      )}
      {decision === Decision.DECLINED && (
        <DeclineDrawer
          isOpen={isOpen}
          setIsOpen={handleClose}
          applicationId={applicationId}
          editedDecision={decisionToEdit}
          onDecisionChanged={setDecision}
          isEditLeadingToCLR={true}
        />
      )}
      {decision === Decision.INELIGIBLE && (
        <IneligibleDrawer
          isOpen={isOpen}
          setIsOpen={handleClose}
          applicationId={applicationId}
          editedDecision={decisionToEdit}
          onDecisionChanged={setDecision}
          isEditLeadingToCLR={true}
        />
      )}
      {decision === Decision.POSTPONED && (
        <PostponeDrawer
          isOpen={isOpen}
          setIsOpen={handleClose}
          applicationId={applicationId}
          editedDecision={decisionToEdit}
          onDecisionChanged={setDecision}
          isEditLeadingToCLR={true}
        />
      )}
      {decision === Decision.WAIVED && (
        <WaiveDrawer
          isOpen={isOpen}
          setIsOpen={handleClose}
          applicationId={applicationId}
          editedDecision={decisionToEdit}
          onDecisionChanged={setDecision}
          isEditLeadingToCLR={true}
        />
      )}
    </div>
  );
};
